// Animated GIF encoder — median-cut palette, Floyd-Steinberg dithering, LZW.
//
// Written rather than pulled in. The blink comparison is the single most
// shareable thing this tool produces, and GIF is the only format that
// autoplays inline everywhere an OSINT report lands. Against that, a new npm
// dependency in the build of a tool people run against their own imagery is a
// supply-chain surface for one well-understood file format, and this codebase
// already owns its pixel math (raster.ts) for the same reason.
//
// The pieces, in the order they run:
//
//   buildPalette   one GLOBAL palette across every frame. Per-frame palettes
//                  would be smaller per frame but make the blink shimmer —
//                  identical ground would land on different colours from one
//                  frame to the next, which is exactly the signal a blink
//                  comparison exists to show. A shared palette also lets every
//                  frame reuse one colour table instead of carrying its own.
//   quantize       nearest palette entry per pixel, with the error pushed into
//                  the neighbours. Satellite scenes are mostly low-contrast
//                  gradients (water, desert, haze) and posterise badly at 256
//                  colours without it.
//   lzwEncode      GIF's variable-width LZW, codes packed LSB-first.
//
// Sizes are what they are: a 900x600 three-frame blink lands around 1-2 MB.

export interface GifFrame {
  /** RGBA, width*height*4, as produced by ctx.getImageData(). */
  data: Uint8ClampedArray;
  /** How long this frame is shown. Clamped to the 10 ms GIF tick. */
  delayMs: number;
}

// A 5-bit-per-channel histogram: 32768 buckets is fine enough that two colours
// sharing a bucket are indistinguishable, and small enough to walk repeatedly
// during the median cut without allocating per-pixel structures.
const HIST_BITS = 5;
const HIST_SIZE = 1 << (HIST_BITS * 3);
const key15 = (r: number, g: number, b: number): number =>
  ((r >> 3) << 10) | ((g >> 3) << 5) | (b >> 3);

interface Box {
  lo: number; hi: number;            // slice of the sorted bucket list
  rMin: number; rMax: number;
  gMin: number; gMax: number;
  bMin: number; bMax: number;
  count: number;
}

function boxBounds(box: Box, buckets: Int32Array, hist: Int32Array): void {
  box.rMin = box.gMin = box.bMin = 255;
  box.rMax = box.gMax = box.bMax = 0;
  box.count = 0;
  for (let i = box.lo; i < box.hi; i++) {
    const k = buckets[i]!;
    const r = ((k >> 10) & 31) << 3;
    const g = ((k >> 5) & 31) << 3;
    const b = (k & 31) << 3;
    if (r < box.rMin) box.rMin = r;
    if (r > box.rMax) box.rMax = r;
    if (g < box.gMin) box.gMin = g;
    if (g > box.gMax) box.gMax = g;
    if (b < box.bMin) box.bMin = b;
    if (b > box.bMax) box.bMax = b;
    box.count += hist[k]!;
  }
}

/** One palette for every frame. Returns packed RGB triples. */
export function buildPalette(frames: GifFrame[], maxColors = 256): Uint8Array {
  const hist = new Int32Array(HIST_SIZE);
  // True colour sums per bucket. Averaging the bucket KEYS instead would take
  // each entry's colour from the bucket's lower corner, biasing the whole
  // palette down by ~4 per channel — invisible on its own, but Floyd-Steinberg
  // then diffuses that constant bias across the frame and it shows up as a
  // measurable darkening. Verified against an independent decoder.
  const sumR = new Float64Array(HIST_SIZE);
  const sumG = new Float64Array(HIST_SIZE);
  const sumB = new Float64Array(HIST_SIZE);
  for (const f of frames) {
    const d = f.data;
    for (let i = 0; i < d.length; i += 4) {
      // Fully transparent pixels contribute no colour. Studio frames are
      // opaque, but a cropped edge or a scene gap can leave holes.
      if (d[i + 3]! < 8) continue;
      const k = key15(d[i]!, d[i + 1]!, d[i + 2]!);
      hist[k]! += 1;
      sumR[k]! += d[i]!;
      sumG[k]! += d[i + 1]!;
      sumB[k]! += d[i + 2]!;
    }
  }
  const buckets: number[] = [];
  for (let k = 0; k < HIST_SIZE; k++) if (hist[k]! > 0) buckets.push(k);
  const used = Int32Array.from(buckets);
  if (used.length === 0) return new Uint8Array(3);   // empty frames: one black

  const boxes: Box[] = [{ lo: 0, hi: used.length, rMin: 0, rMax: 0, gMin: 0,
                          gMax: 0, bMin: 0, bMax: 0, count: 0 }];
  boxBounds(boxes[0]!, used, hist);

  while (boxes.length < maxColors) {
    // Split the box with the largest side that still holds more than one
    // bucket. Splitting by pixel count alone lets a huge flat sky eat the
    // palette; splitting by extent keeps rare-but-distinct colours alive.
    let target = -1;
    let best = 0;
    for (let i = 0; i < boxes.length; i++) {
      const b = boxes[i]!;
      if (b.hi - b.lo < 2) continue;
      const side = Math.max(b.rMax - b.rMin, b.gMax - b.gMin, b.bMax - b.bMin);
      const score = side * Math.cbrt(b.count);
      if (score > best) { best = score; target = i; }
    }
    if (target < 0) break;

    const box = boxes[target]!;
    const rSide = box.rMax - box.rMin;
    const gSide = box.gMax - box.gMin;
    const bSide = box.bMax - box.bMin;
    const shift = rSide >= gSide && rSide >= bSide ? 10 : gSide >= bSide ? 5 : 0;
    const slice = Array.from(used.subarray(box.lo, box.hi));
    slice.sort((x, y) => ((x >> shift) & 31) - ((y >> shift) & 31));
    used.set(slice, box.lo);

    // Cut at the median by PIXEL count, not by bucket count — half the buckets
    // can hold a tiny fraction of the image.
    const half = box.count / 2;
    let acc = 0;
    let cut = box.lo + 1;
    for (let i = box.lo; i < box.hi - 1; i++) {
      acc += hist[used[i]!]!;
      if (acc >= half) { cut = i + 1; break; }
      cut = i + 2;
    }
    // BOTH halves must be non-empty. When the running count only crosses the
    // median at the final bucket, cut lands on box.hi and the "split" produces
    // an empty box: it consumes a palette slot, leaves the original box
    // untouched, and repeats until the budget is gone — so a five-colour image
    // came back with two of its colours averaged together.
    cut = Math.min(Math.max(cut, box.lo + 1), box.hi - 1);
    const right: Box = { lo: cut, hi: box.hi, rMin: 0, rMax: 0, gMin: 0,
                         gMax: 0, bMin: 0, bMax: 0, count: 0 };
    box.hi = cut;
    boxBounds(box, used, hist);
    boxBounds(right, used, hist);
    boxes.push(right);
  }

  const palette = new Uint8Array(boxes.length * 3);
  boxes.forEach((box, i) => {
    let r = 0; let g = 0; let b = 0; let n = 0;
    for (let j = box.lo; j < box.hi; j++) {
      const k = used[j]!;
      r += sumR[k]!;
      g += sumG[k]!;
      b += sumB[k]!;
      n += hist[k]!;
    }
    palette[i * 3] = n ? Math.round(r / n) : 0;
    palette[i * 3 + 1] = n ? Math.round(g / n) : 0;
    palette[i * 3 + 2] = n ? Math.round(b / n) : 0;
  });
  return palette;
}

/** Nearest-entry lookup, memoised on the 5-bit key so a full-HD frame does not
 *  run 2M linear scans over 256 colours. */
function nearestFactory(palette: Uint8Array): (r: number, g: number, b: number) => number {
  const cache = new Int16Array(HIST_SIZE).fill(-1);
  const n = palette.length / 3;
  return (r, g, b) => {
    const k = key15(r, g, b);
    const hit = cache[k]!;
    if (hit >= 0) return hit;
    let bestI = 0;
    let bestD = Infinity;
    for (let i = 0; i < n; i++) {
      const dr = r - palette[i * 3]!;
      const dg = g - palette[i * 3 + 1]!;
      const db = b - palette[i * 3 + 2]!;
      const d = dr * dr + dg * dg + db * db;
      if (d < bestD) { bestD = d; bestI = i; }
    }
    cache[k] = bestI;
    return bestI;
  };
}

/** RGBA -> palette indices, Floyd-Steinberg error diffusion. */
export function quantize(data: Uint8ClampedArray, width: number, height: number,
                         palette: Uint8Array, dither = true): Uint8Array {
  const out = new Uint8Array(width * height);
  const nearest = nearestFactory(palette);
  if (!dither) {
    for (let i = 0, p = 0; p < out.length; i += 4, p++) {
      out[p] = nearest(data[i]!, data[i + 1]!, data[i + 2]!);
    }
    return out;
  }
  // Two running error rows, so the whole image is never held as floats.
  let errA = new Float32Array(width * 3 + 6);
  let errB = new Float32Array(width * 3 + 6);
  for (let y = 0; y < height; y++) {
    errB.fill(0);
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      const e = x * 3 + 3;
      const r = Math.max(0, Math.min(255, data[i]! + errA[e]!));
      const g = Math.max(0, Math.min(255, data[i + 1]! + errA[e + 1]!));
      const b = Math.max(0, Math.min(255, data[i + 2]! + errA[e + 2]!));
      const idx = nearest(r, g, b);
      out[y * width + x] = idx;
      const dr = r - palette[idx * 3]!;
      const dg = g - palette[idx * 3 + 1]!;
      const db = b - palette[idx * 3 + 2]!;
      // 7/16 right, 3/16 below-left, 5/16 below, 1/16 below-right.
      errA[e + 3]! += dr * 0.4375; errA[e + 4]! += dg * 0.4375; errA[e + 5]! += db * 0.4375;
      errB[e - 3]! += dr * 0.1875; errB[e - 2]! += dg * 0.1875; errB[e - 1]! += db * 0.1875;
      errB[e]! += dr * 0.3125; errB[e + 1]! += dg * 0.3125; errB[e + 2]! += db * 0.3125;
      errB[e + 3]! += dr * 0.0625; errB[e + 4]! += dg * 0.0625; errB[e + 5]! += db * 0.0625;
    }
    const swap = errA; errA = errB; errB = swap;
  }
  return out;
}

/** GIF's LZW: variable code width, codes packed LSB-first into 255-byte
 *  sub-blocks. The dictionary resets at 4096 codes, which is what keeps the
 *  code width inside 12 bits. */
export function lzwEncode(indices: Uint8Array, minCodeSize: number): Uint8Array {
  const clear = 1 << minCodeSize;
  const eoi = clear + 1;
  const out: number[] = [];
  let cur = 0;      // bit accumulator
  let curBits = 0;
  let width = minCodeSize + 1;
  let next = eoi + 1;
  let dict = new Map<number, number>();

  const emit = (code: number): void => {
    cur |= code << curBits;
    curBits += width;
    while (curBits >= 8) {
      out.push(cur & 0xff);
      cur >>= 8;
      curBits -= 8;
    }
  };

  emit(clear);
  let prefix = indices.length ? indices[0]! : eoi;
  for (let i = 1; i < indices.length; i++) {
    const k = indices[i]!;
    // (prefix, k) packs into one number: prefix < 4096, k < 256.
    const combo = (prefix << 8) | k;
    const found = dict.get(combo);
    if (found !== undefined) { prefix = found; continue; }
    emit(prefix);
    dict.set(combo, next);
    next += 1;
    if (next > 4095) {
      emit(clear);
      dict = new Map();
      next = eoi + 1;
      width = minCodeSize + 1;
    } else if (next > (1 << width)) {
      width += 1;
    }
    prefix = k;
  }
  if (indices.length) emit(prefix);
  emit(eoi);
  if (curBits > 0) out.push(cur & 0xff);

  // Sub-blocks: one length byte per <=255 bytes, terminated by a zero byte.
  const blocked: number[] = [];
  for (let i = 0; i < out.length; i += 255) {
    const chunk = out.slice(i, i + 255);
    blocked.push(chunk.length, ...chunk);
  }
  blocked.push(0);
  return Uint8Array.from(blocked);
}

export interface GifOptions {
  /** 0 = forever (the default), N = play N times. */
  loops?: number;
  dither?: boolean;
}

/** Assemble an animated GIF89a. */
export function encodeGif(frames: GifFrame[], width: number, height: number,
                          opts: GifOptions = {}): Uint8Array {
  if (!frames.length) throw new Error('A GIF needs at least one frame.');
  const palette = buildPalette(frames);
  const colors = palette.length / 3;
  // The colour table has to be a power of two, at least 2 entries.
  let bits = 1;
  while ((1 << bits) < colors) bits += 1;
  const tableSize = 1 << bits;

  const bytes: number[] = [];
  const push = (...v: number[]): void => { for (const x of v) bytes.push(x); };
  const short = (v: number): void => { bytes.push(v & 0xff, (v >> 8) & 0xff); };

  push(0x47, 0x49, 0x46, 0x38, 0x39, 0x61);              // "GIF89a"
  short(width); short(height);
  push(0x80 | ((bits - 1) & 7), 0, 0);                    // GCT present, sorted flag off
  for (let i = 0; i < tableSize; i++) {
    push(palette[i * 3] ?? 0, palette[i * 3 + 1] ?? 0, palette[i * 3 + 2] ?? 0);
  }
  // NETSCAPE2.0 looping extension — without it every viewer plays once.
  push(0x21, 0xff, 11, 0x4e, 0x45, 0x54, 0x53, 0x43, 0x41, 0x50, 0x45,
       0x32, 0x2e, 0x30, 3, 1);
  short(opts.loops ?? 0);
  push(0);

  const minCodeSize = Math.max(2, bits);
  for (const frame of frames) {
    // Delay is centiseconds. Below 2, most viewers silently substitute 10,
    // so clamp rather than let a fast blink turn into a slow one.
    const delay = Math.max(2, Math.round(frame.delayMs / 10));
    push(0x21, 0xf9, 4, 0x04);                            // GCE, dispose=background
    short(delay);
    push(0, 0);                                           // no transparent index
    push(0x2c);                                           // image descriptor
    short(0); short(0); short(width); short(height);
    push(0);                                              // no local table, no interlace
    push(minCodeSize);
    const idx = quantize(frame.data, width, height, palette, opts.dither ?? true);
    for (const b of lzwEncode(idx, minCodeSize)) bytes.push(b);
  }
  push(0x3b);                                             // trailer
  return Uint8Array.from(bytes);
}
